import { Link } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Composer } from "@/components/Composer";
import { PostCard } from "@/components/PostCard";
import { Avatar } from "@/components/Avatar";

const PAGE = 15;

const GuestHero = () => (
  <div className="grain relative overflow-hidden rounded-[2rem] bg-[#181615] p-8 text-white sm:p-10">
    <p className="font-mono text-xs uppercase tracking-[0.2em] text-pink-300">Nailora</p>
    <h1 className="mt-3 font-serif text-3xl leading-tight sm:text-4xl">Fresh sets from real nail techs. Book in their DMs.</h1>
    <div className="mt-6 flex flex-wrap gap-3">
      <Link to="/join" data-testid="hero-join-button" className="ig-gradient rounded-full px-6 py-3 text-sm font-medium text-white">Join with Instagram</Link>
      <Link to="/explore" data-testid="hero-find-tech-button" className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm text-stone-900">Find a nail tech <ArrowRight className="h-4 w-4" /></Link>
    </div>
  </div>
);

const TechStrip = () => {
  const { data: techs = [] } = useQuery({ queryKey: ["techs", "strip"], queryFn: () => api.get("/techs", { params: { limit: 12 } }).then((r) => r.data) });
  return (
    <div data-testid="tech-strip" className="flex gap-4 overflow-x-auto pb-2">
      {techs.map((t) => (
        <Link key={t.id} to={`/tech/${t.username}`} className="flex w-16 shrink-0 flex-col items-center gap-1.5">
          <div className="ig-ring"><div className="rounded-full border-2 border-[#FAF7F2]"><Avatar user={t} className="h-14 w-14" textClass="text-lg" /></div></div>
          <span className="w-full truncate text-center text-[11px] text-stone-600">{t.username}</span>
        </Link>
      ))}
    </div>
  );
};

export default function Feed() {
  const { user } = useAuth();
  const q = useInfiniteQuery({
    queryKey: ["feed"],
    initialPageParam: "",
    queryFn: ({ pageParam }) => api.get("/posts", { params: { before: pageParam, limit: PAGE } }).then((r) => r.data),
    getNextPageParam: (last) => (last.length === PAGE ? last[last.length - 1].created_at : undefined),
  });
  const posts = q.data?.pages.flat() || [];
  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-8 sm:px-6">
      {!user && <GuestHero />}
      <TechStrip />
      {user && <Composer user={user} onPosted={() => q.refetch()} />}
      {posts.map((p) => <PostCard key={p.id} post={p} onDeleted={() => q.refetch()} />)}
      {!q.isLoading && posts.length === 0 && <p data-testid="feed-empty" className="py-16 text-center text-stone-500">No posts yet — be the first to share a set.</p>}
      {q.hasNextPage && (
        <button data-testid="feed-load-more" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage} className="w-full rounded-full border border-stone-300 py-3 text-sm hover:border-stone-900">
          {q.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}
